import React from 'react';
import { Box, Icon, IOption, Typography, useTheme } from '../../../base';
import { DASHBOARD_TIME_RANGES, IStatsCard } from '../../../common';

import {
    StyledCard,
    StyledPercentContainer,
    StyledValueContainer,
    StyledValueColumnWrapper,
    StyledContentWrapper,
} from './StatsCard.styles';
import { useTranslation } from 'react-i18next';

interface StatsCardProps {
    statsData: IStatsCard;
    timePeriod: IOption;
}

const StatsCard: React.FC<StatsCardProps> = ({ statsData, timePeriod }) => {
    const { title, value, change } = statsData;
    const { t } = useTranslation();
    const theme = useTheme();

    const getChangeColor = () => {
        if (!change || change.isZero) return theme.palette.text.secondary;
        return change.isPositive
            ? theme.palette.success.main
            : theme.palette.error.main;
    };

    const renderChangeIndicator = () => {
        if (!change) return null;

        const { value: changeValue, isPositive, isZero } = change;

        if (isZero) {
            return (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem' }}
                >
                    {timePeriod.value === DASHBOARD_TIME_RANGES.YEAR
                        ? t('dashboardPage.noChangesYear', {
                              timePeriod: timePeriod.label.toLowerCase(),
                              interpolation: { escapeValue: false }, // Disable escaping
                          })
                        : t('dashboardPage.noChanges', {
                              timePeriod: timePeriod.label.toLowerCase(),
                              interpolation: { escapeValue: false }, // Disable escaping
                          })}
                </Typography>
            );
        }

        const color = isPositive
            ? theme.palette.success.main
            : theme.palette.error.main;

        return (
            <StyledPercentContainer>
                <Icon
                    name={isPositive ? 'MovingIcon' : 'TrendingDownIcon'}
                    sx={{ color, fontSize: 12, marginInlineEnd: 0.5 }}
                />
                <Typography
                    variant="body2"
                    sx={{
                        color,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                    }}
                >
                    {`${changeValue}% ${isPositive ? t('dashboardPage.up') : t('dashboardPage.down')} ${t('dashboardPage.fromLastWeek')}`}
                </Typography>
            </StyledPercentContainer>
        );
    };

    return (
        <StyledCard>
            <StyledContentWrapper>
                <StyledValueColumnWrapper>
                    <StyledValueContainer sx={{ color: getChangeColor() }}>
                        {value}
                    </StyledValueContainer>
                </StyledValueColumnWrapper>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flex: 1,
                    }}
                >
                    <Typography
                        variant="h4"
                        color="text.secondary"
                        sx={{
                            fontWeight: 600,
                            fontSize: '1.25rem',
                            lineHeight: 1.1,
                        }}
                    >
                        {title}
                    </Typography>
                    {renderChangeIndicator()}
                </Box>
            </StyledContentWrapper>
        </StyledCard>
    );
};

export default StatsCard;
